import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MessageCircle, Instagram, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { getWhatsAppLink } from '../lib/utils';

const LOGO_URL = '/assets/brand/logo.png';
const WHATSAPP = '5545999092653';
const PHONE = '+55 45 99909-2653';
const EMAIL = 'contato@valmircapeletti.com';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) return null;

  const navLinks = [
    { href: '#hero', label: 'Início' },
    { href: '#search', label: 'Buscar' },
    { href: '#featured', label: 'Destaques' },
    { href: '#categories', label: 'Categorias' },
    { href: '#mission', label: 'Sobre' },
    { href: '#contact', label: 'Contato' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-sm">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" onClick={(e) => scrollToSection(e, '#hero')} className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Valmir Capeletti - Negócios Imobiliários"
              className="h-32 md:h-40 w-auto"
              data-testid="header-logo"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-slate-600 hover:text-brand-blue font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              data-testid="header-whatsapp-btn"
              asChild
              className="bg-green-600 hover:bg-green-700 text-white rounded-full h-10 px-5"
            >
              <a href={getWhatsAppLink(WHATSAPP)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </a>
            </Button>
            <Button
              data-testid="header-call-btn"
              asChild
              variant="outline"
              className="rounded-full h-10 px-5 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
            >
              <a href={`tel:${PHONE}`}>
                <Phone className="w-4 h-4 mr-2" />
                Ligar
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="py-3 px-4 text-slate-600 hover:text-brand-blue hover:bg-slate-50 rounded-lg font-medium transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-slate-100">
                <Button
                  asChild
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                >
                  <a href={getWhatsAppLink(WHATSAPP)} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-brand-blue text-brand-blue"
                >
                  <a href={`tel:${PHONE}`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Ligar
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) return null;

  const navLinks = [
    { href: '#hero', label: 'Início' },
    { href: '#search', label: 'Buscar Imóveis' },
    { href: '#featured', label: 'Destaques' },
    { href: '#categories', label: 'Categorias' },
    { href: '#contact', label: 'Contato' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-brand-blue text-white py-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src={LOGO_URL}
              alt="Valmir Capeletti - Negócios Imobiliários"
              className="h-32 w-auto mb-4 brightness-0 invert"
              data-testid="footer-logo"
            />
            <p className="text-slate-300 mb-4 max-w-md">
              Sua imobiliária de confiança em Toledo e região. 
              Compra, venda e investimento com atendimento personalizado e transparente.
            </p>
            <div className="text-sm text-slate-400">
              CRECI: 9322-J
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Links Rápidos</h4>
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-slate-300 hover:text-brand-gold transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Contato</h4>
            <div className="space-y-3 text-slate-300">
              <a href={`tel:${PHONE}`} className="flex items-center gap-2 hover:text-brand-gold transition-colors">
                <Phone className="w-4 h-4" />
                {PHONE}
              </a>
              <a href={getWhatsAppLink(WHATSAPP)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-brand-gold transition-colors">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-brand-gold transition-colors">
                <Mail className="w-4 h-4" />
                {EMAIL}
              </a>
              <a 
                href="https://www.instagram.com/capelettivalmir/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                <Instagram className="w-4 h-4" />
                 Instagram
              </a>

              <a 
                href="https://www.facebook.com/valmircapeletticorretor" 
                target="_blank" 
                rel="noopener noreferrer"
                 className="flex items-center gap-2 hover:text-brand-gold transition-colors"
              >
                 <Facebook className="w-4 h-4" />
                 Facebook
              </a>

              <div className="pt-2 text-sm">
                <p>Rua Santos Dumont, 15646, Sala 02</p>
                <p>Centro, Toledo – PR</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Valmir Capeletti – Negócios Imobiliários. Todos os direitos reservados.
          </p>
          <Link to="/admin" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
            Área Administrativa
          </Link>
        </div>
      </div>
    </footer>
  );
};
